import React, { Component } from 'react';
import Select from '../../common/select';
import BootstrapTable from 'react-bootstrap-table-next';
import "react-bootstrap-table/css/react-bootstrap-table.css";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { HTTPService } from "../../../services/httpService";
import { TemplateModal } from './TemplateModal'; // Import the TemplateModal component

const API = new HTTPService();

export class TemplateList extends Component {
    state = {
        templates: [],
        filteredTemplates: [],
        nameSearchTerm: "",
        filteredByName: [],
        showNameFilter: false,
        selectedDealership: "All Dealerships", 
        dealershipOptions: ["All Dealerships"],
        dealerships: [],
        showModal: false,
        modalAction: "",
        selectedTemplate: {},
        loading: false,
        // Dealership list modal
        showDealershipModal: false,
        selectedTemplateDealerships: [],
        // Error handling
        error: null,
        // Search loading
        searchLoading: false
    };

    async componentDidMount() {
        await this.loadTemplates();
        await this.loadDealerships();
    }

    // =====================================================
    // API DATA LOADING
    // =====================================================

    // Load all templates for this specialist
    async loadTemplates() {
        try {
            this.setState({ loading: true, error: null });
            const templatesRequest = `/templates/${this.props.typeId}`;
            const { data: templatesResponse } = await API.get(templatesRequest);
            
            // Extract templates from response (handle both direct array and wrapped response)
            const templates = templatesResponse.data || templatesResponse;
            
            // Add computed fields for display compatibility
            templates.forEach(template => {
                template.displayName = template.template_name;
                template.createdDate = moment(template.created_date).format("YYYY-MM-DD");
                // Service count and dealership count come from the API response
                template.dealershipCount = template.dealership_count || 0;
                template.serviceCount = template.service_count || 0;
                template.dealershipList = []; // Will be loaded separately when needed
            });

            this.setState({ templates, loading: false });
            //console.log("Templates loaded:", templates);
        } catch (error) {
            console.error("Error loading templates:", error);
            this.setState({ 
                loading: false,
                error: "Failed to load templates. Please refresh the page.",
                templates: [],
                showNameFilter: false,
                filteredByName: []
            });
        }
    }

    // Load dealerships for filtering
    async loadDealerships() {
        try {
            const dealershipsRequest = `/dealerships/${this.props.typeId}`;
            const { data: dealershipsResponse } = await API.get(dealershipsRequest);
            
            // Extract dealerships from response
            const dealerships = dealershipsResponse.data || dealershipsResponse;
            
            const dealershipOptions = ["All Dealerships"];
            dealerships.forEach(dealer => {
                dealershipOptions.push(dealer.dealer_name);
            });

            this.setState({ dealerships, dealershipOptions });
        } catch (error) {
            console.error("Error loading dealerships:", error);
            // Fallback to basic options
            this.setState({ 
                dealershipOptions: ["All Dealerships"]
            });
        }
    }

    // =====================================================
    // SEARCH AND FILTERING
    // =====================================================

    // Handle template name search with API
    handleNameSearchChange = async (event) => {
        const { value } = event.currentTarget;
        
        if (!value.trim()) {
            this.clearNameFilter();
            return;
        }
        
        this.setState({ nameSearchTerm: value, searchLoading: true });
        
        try {
            // Use API search endpoint
            const response = await API.get(`/templates/${this.props.typeId}/search?name=${encodeURIComponent(value)}`);
            const searchResponse = response.data;
            const filtered = searchResponse.data || searchResponse;
            
            // Add computed fields for display compatibility
            filtered.forEach(template => {
                template.displayName = template.template_name;
                template.createdDate = moment(template.created_date).format("YYYY-MM-DD");
                template.dealershipCount = template.dealership_count || 0;
                template.serviceCount = template.service_count || 0;
            });
            
            this.setState({ 
                filteredByName: filtered,
                showNameFilter: true,
                searchLoading: false
            });
        } catch (error) {
            console.error("Error searching templates:", error);
            // Fallback to client-side filtering
            const lowercasedValue = value.toLowerCase().trim();
            const filtered = this.state.templates.filter(template => 
                template.template_name && template.template_name.toLowerCase().includes(lowercasedValue)
            );
            
            this.setState({ 
                filteredByName: filtered,
                showNameFilter: true,
                searchLoading: false
            });
        }
    };

    // Clear name filter
    clearNameFilter = () => {
        this.setState({ 
            nameSearchTerm: "", 
            filteredByName: [], 
            showNameFilter: false,
            searchLoading: false
        });
    };

    // Handle dealership filter with API
    handleDealershipChange = async (event) => {
        const { value } = event.currentTarget;
        this.setState({ selectedDealership: value });
        
        // If filtering by dealership and not showing name filter, use API
        if (value !== "All Dealerships" && !this.state.showNameFilter) {
            try {
                this.setState({ loading: true });
                const response = await API.get(`/templates/${this.props.typeId}/search?dealership=${encodeURIComponent(value)}`);
                const searchResponse = response.data;
                const filtered = searchResponse.data || searchResponse;
                
                // Add computed fields for display compatibility
                filtered.forEach(template => {
                    template.displayName = template.template_name;
                    template.createdDate = moment(template.created_date).format("YYYY-MM-DD");
                    template.dealershipCount = template.dealership_count || 0;
                    template.serviceCount = template.service_count || 0;
                });
                
                this.setState({ templates: filtered, loading: false });
            } catch (error) {
                console.error("Error filtering templates by dealership:", error);
                this.setState({ loading: false });
            }
        } else if (value === "All Dealerships" && !this.state.showNameFilter) {
            // Reload all templates when "All Dealerships" is selected
            await this.loadTemplates();
        }
    };

    // =====================================================
    // TEMPLATE MODAL MANAGEMENT
    // =====================================================

    // Show dealership list modal with API data
    showDealershipList = async (template) => {
        try {
            const response = await API.get(`/templates/${this.props.typeId}/${template.template_id}/dealerships`);
            const dealershipsResponse = response.data;
            
            // Extract dealerships from response
            const dealerships = dealershipsResponse.data || dealershipsResponse;
            
            // Transform dealership data to match existing modal structure
            const dealershipList = dealerships.map(d => ({
                dealership_id: d.dealer_id,
                dealership_name: d.dealer_name,
                location: d.dealer_address_city && d.dealer_address_state 
                    ? `${d.dealer_address_city}, ${d.dealer_address_state}` 
                    : 'Location not available'
            }));

            this.setState({
                selectedTemplateDealerships: dealershipList,
                selectedTemplate: template,
                showDealershipModal: true
            });
        } catch (error) {
            console.error("Error loading template dealerships:", error);
            // Show error but still open modal with empty state
            this.setState({
                selectedTemplateDealerships: [],
                selectedTemplate: template,
                showDealershipModal: true
            });
        }
    };

    // Close dealership list modal
    closeDealershipModal = () => {
        this.setState({
            showDealershipModal: false,
            selectedTemplateDealerships: []
        });
    };

    // Show template details/edit modal
    showTemplateDetails = (template) => {
        this.setState({ 
            selectedTemplate: template, 
            showModal: true, 
            modalAction: "view" 
        });
    };

    // Show add template modal
    showAddTemplate = () => {
        this.setState({ 
            selectedTemplate: {}, 
            showModal: true, 
            modalAction: "add" 
        });
    };

    // Show edit template modal with full details
    showEditTemplate = async (template) => {
        try {
            // Load full template details including services
            const response = await API.get(`/templates/${this.props.typeId}/${template.template_id}/detail`);
            const templateDetailsResponse = response.data;
            
            // Extract template details from response
            const templateDetails = templateDetailsResponse.data || templateDetailsResponse;
            
            this.setState({ 
                selectedTemplate: templateDetails, 
                showModal: true, 
                modalAction: "edit" 
            });
        } catch (error) {
            console.error("Error loading template details:", error);
            // Fallback to basic template data
            this.setState({ 
                selectedTemplate: template, 
                showModal: true, 
                modalAction: "edit" 
            });
        }
    };

    // Close modal
    handleCloseModal = () => {
        this.setState({ 
            showModal: false, 
            selectedTemplate: {} 
        });
    };

    // Handle successful save - reload templates
    handleTemplateSave = () => {
        this.loadTemplates();
        this.handleCloseModal();
    };

    // =====================================================
    // TEMPLATE ACTIONS
    // =====================================================

    // Delete template
    deleteTemplate = async (template) => {
        if (!window.confirm(`Are you sure you want to delete the template "${template.template_name}"?`)) {
            return;
        }

        try {
            await API.delete(`/templates/${this.props.typeId}/${template.template_id}`);
            
            // Reload templates after successful deletion
            await this.loadTemplates();
            
            // Show success message (you might want to use a toast notification)
            alert("Template deleted successfully");
        } catch (error) {
            console.error("Error deleting template:", error);
            
            // Handle specific error messages
            let errorMessage = "Failed to delete template";
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
            }
            
            alert(errorMessage);
        }
    };

    // =====================================================
    // RENDER METHODS
    // =====================================================

    // Render name search component
    renderNameSearch = () => {
        const templateNames = [...new Set(
            this.state.templates
                .filter(t => t.template_name)
                .map(t => t.template_name)
        )];
        
        return (
            <div className="form-group">
                <label htmlFor="nameSearch">Search Template Name:</label>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        id="nameSearch"
                        list="nameOptions"
                        placeholder="Type to search template names..."
                        value={this.state.nameSearchTerm}
                        onChange={this.handleNameSearchChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                this.clearNameFilter();
                            }
                        }}
                        disabled={this.state.searchLoading}
                    />
                    {this.state.nameSearchTerm && (
                        <div className="input-group-append">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={this.clearNameFilter}
                                type="button"
                                disabled={this.state.searchLoading}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    )}
                    {this.state.searchLoading && (
                        <div className="input-group-append">
                            <span className="input-group-text">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="sr-only">Searching...</span>
                                </div>
                            </span>
                        </div>
                    )}
                </div>
                <datalist id="nameOptions">
                    {templateNames.map((name, idx) => (
                        <option key={idx} value={name} />
                    ))}
                </datalist>
            </div>
        );
    };

    // Render error state
    renderError = () => {
        if (!this.state.error) return null;
        
        return (
            <div className="alert alert-danger" role="alert">
                <h6 className="alert-heading">Error Loading Templates</h6>
                <p>{this.state.error}</p>
                <hr />
                <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => {
                        this.setState({ error: null });
                        this.loadTemplates();
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    };

    render() {
        // Apply filters to get display data
        let displayTemplates = [];
        
        // Priority: Name filter > Regular templates
        if (this.state.showNameFilter) {
            displayTemplates = this.state.filteredByName;
        } else {
            displayTemplates = this.state.templates;
        }

        // Table columns
        const columns = [{
            dataField: 'template_name',
            text: "Template Name",
            formatter: (cell, row) => (
                <a
                    href="#/"
                    onClick={(e) => {
                        e.preventDefault();
                        this.showEditTemplate(row);
                    }}
                    style={{
                        cursor: 'pointer',
                        color: '#007bff',
                        textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {cell}
                </a>
            )
        }, {
            dataField: 'dealershipCount',
            text: "Dealerships",
            formatter: (cell, row) => {
                if (cell === 0) {
                    return <span className="text-muted">None assigned</span>;
                } else {
                    return (
                        <a
                            href="#/"
                            onClick={(e) => {
                                e.preventDefault();
                                this.showDealershipList(row);
                            }}
                            className="text-primary"
                        >
                            {cell} dealership{cell !== 1 ? 's' : ''}
                        </a>
                    );
                }
            }
        }, {
            dataField: 'serviceCount',
            text: "Services",
            formatter: (cell) => cell || 0
        }, {
            dataField: 'createdDate',
            text: "Created"
        }, {
            dataField: 'actions',
            text: "Actions",
            formatter: (cell, row) => (
                <button 
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => this.deleteTemplate(row)}
                    title="Delete Template"
                >
                    <i className="fa fa-trash"></i>
                </button>
            )
        }];

        // Row styling
        const rowStyle = (row, rowIndex) => {
            return { backgroundColor: '#f8f9fa' };
        };

        const divStyle = { backgroundColor: "#dde3e9" };

        return (
            <React.Fragment>
                {/* Header */}
                <div className="container-flex m-3">
                    <div className="row" style={divStyle}>
                        <div className="col text-left">
                            <Button 
                                variant="success" 
                                onClick={this.showAddTemplate}
                                disabled={this.state.loading}
                            >
                                <i className="fa fa-plus"></i> ADD NEW TEMPLATE
                            </Button>
                        </div>
                        <div className="col text-center">
                            {this.state.showNameFilter ? (
                                <h6>Name Filter Results ({displayTemplates.length} Template{displayTemplates.length !== 1 ? 's' : ''})</h6>
                            ) : (
                                <h6>All Templates ({displayTemplates.length} Template{displayTemplates.length !== 1 ? 's' : ''})</h6>
                            )}
                        </div>
                        <div className="col text-right">
                            {this.state.loading && (
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Error Display */}
                    {this.renderError()}
                    
                    {/* Search and Filter Row */}
                    <div className="row mb-3">
                        <div className="col-4">
                            {this.renderNameSearch()}
                        </div>
                        <div className="col-8">
                            <div className="row align-items-end h-100">
                                <div className="col-6">
                                    <Select
                                        name="dealershipFilter"
                                        label="Filter by Dealership"
                                        options={this.state.dealershipOptions}
                                        value={this.state.selectedDealership}
                                        onChange={this.handleDealershipChange}
                                        disabled={this.state.showNameFilter || this.state.loading}
                                    />
                                </div>
                                <div className="col-6">
                                    {(this.state.showNameFilter || this.state.selectedDealership !== "All Dealerships") && (
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                this.clearNameFilter();
                                                this.setState({ selectedDealership: "All Dealerships" });
                                                this.loadTemplates();
                                            }}
                                        >
                                            Clear All Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Search Result Messages */}
                    {this.state.showNameFilter && (
                        <div className={`alert ${this.state.filteredByName.length === 0 ? 'alert-warning' : 'alert-info'}`}>
                            {this.state.filteredByName.length === 0 ? (
                                <>No templates found for name "{this.state.nameSearchTerm}".</>
                            ) : (
                                <>Found {this.state.filteredByName.length} template{this.state.filteredByName.length !== 1 ? 's' : ''} for name "{this.state.nameSearchTerm}".</>
                            )}
                            <button className="btn btn-link p-0 ml-2" onClick={this.clearNameFilter}>CLEAR FILTER</button>
                        </div>
                    )}
                </div>

                {/* Templates Table */}
                {!this.state.error && (
                    <div className="container-flex m-3" style={{background: "#ffffff"}}>
                        <div className="row">
                            <div className="col text-center">
                                <BootstrapTable
                                    keyField='template_id'
                                    data={displayTemplates}
                                    columns={columns}
                                    rowStyle={rowStyle}
                                    striped
                                    hover
                                    noDataIndication={() => (
                                        <div className="text-center py-4">
                                            {this.state.loading ? (
                                                <div>
                                                    <div className="spinner-border" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                    <p className="mt-2">Loading templates...</p>
                                                </div>
                                            ) : (
                                                <div>
                                                    <i className="fa fa-file-text-o fa-3x text-muted mb-3"></i>
                                                    <p className="text-muted">No templates found</p>
                                                    {!this.state.showNameFilter && this.state.selectedDealership === "All Dealerships" && (
                                                        <button 
                                                            className="btn btn-primary"
                                                            onClick={this.showAddTemplate}
                                                        >
                                                            Create Your First Template
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Template Modal */}
                <TemplateModal
                    show={this.state.showModal}
                    onHide={this.handleCloseModal}
                    template={this.state.selectedTemplate}
                    action={this.state.modalAction}
                    typeId={this.props.typeId}
                    onSave={this.handleTemplateSave}
                />

                {/* Dealership List Modal */}
                <Modal 
                    show={this.state.showDealershipModal} 
                    onHide={this.closeDealershipModal} 
                    size="lg" 
                    centered
                    style={{ zIndex: 1050 }}
                >
                    <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
                        <Modal.Title>
                            Dealerships using "{this.state.selectedTemplate.template_name}"
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: 0, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                        {this.state.selectedTemplateDealerships.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fa fa-building-o fa-3x text-muted mb-3"></i>
                                <p className="text-muted mb-0">No dealerships are currently using this template.</p>
                            </div>
                        ) : (
                            <>
                                <div className="px-3 py-2 bg-light border-bottom" style={{ flexShrink: 0 }}>
                                    <small className="text-muted">
                                        {this.state.selectedTemplateDealerships.length} dealership{this.state.selectedTemplateDealerships.length !== 1 ? 's' : ''}
                                    </small>
                                </div>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto', 
                                    minHeight: 0,
                                    maxHeight: 'calc(70vh - 120px)' 
                                }}>
                                    {this.state.selectedTemplateDealerships.map((dealership, index) => (
                                        <div 
                                            key={dealership.dealership_id || index} 
                                            className="px-3 py-3"
                                            style={{ 
                                                borderBottom: index < this.state.selectedTemplateDealerships.length - 1 ? '1px solid #dee2e6' : 'none',
                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                borderLeft: '3px solid transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderLeftColor = '#007bff';
                                                e.currentTarget.style.backgroundColor = '#e3f2fd';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderLeftColor = 'transparent';
                                                e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                                            }}
                                        >
                                            <div style={{ fontSize: '16px', fontWeight: 500 }}>
                                                {dealership.dealership_name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                        <Button variant="secondary" onClick={this.closeDealershipModal}>
                            CLOSE
                        </Button>
                    </Modal.Footer>
                </Modal>
            </React.Fragment>
        );
    }
}

export default TemplateList;